<template>
  <div class="min-height">
    <TableLoading v-if="branches.objects.length === 0"/>
    <div v-else>
      <form @submit.prevent="createOrder">
        <div class="row">
          <div class="col-sm-12 d-flex align-items-center" v-if="checkForPermission(ignore_order_timezone_permission)">
            <div class="mb-3">
              <div class="form-check form-check-right mb-3">
                <input
                    class="form-check-input"
                    type="checkbox"
                    @change="ignoreTimezone"
                />
                <label class="form-check-label">
                  Ignore Timezone Order Limit
                </label>
              </div>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="mb-3">
              <label>Customer (Branch)</label>
              <multiselect v-model="customer" :options="branches.objects" :class="{'is-invalid': submitted && v$.order.customer.$error}"
                           class="multiselect-height-3" placeholder="Select one" label="name" track-by="name" :searchable="true">
                <span slot="noResult">No branch found. Change search query.</span>
              </multiselect>
              <p
                  v-for="error of v$.order.customer.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
              >
                {{error.$message}}
              </p>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="mb-3">
              <label>Ordered By</label>
              <input
                  v-model="order.orderedBy"
                  name="name"
                  type="text"
                  class="form-control py-3"
                  :class="{
                        'is-invalid': submitted && v$.order.orderedBy.$error,
                      }"
              />
              <p
                  v-for="error of v$.order.orderedBy.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
              >
                {{error.$message}}
              </p>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="mb-3">
              <label>Delivery Date</label>
              <DatePicker v-if="customer" v-model="deliveryDate" :disabled-dates="disabledDates" format="yyyy-MM-dd"></DatePicker>
              <input v-else class="form-control py-3" disabled/>
              <p
                  v-for="error of v$.deliveryDate.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
              >
                {{error.$message}}
              </p>
            </div>
          </div>
          <div class="col-sm-6">
            <div class="mb-3">
              <label>Timezone</label>
              <multiselect v-model="order.orderTimezone" :options="availableTimezones" :class="{
                        'is-invalid': submitted && v$.order.orderTimezone.$error,
                  }" class="multiselect-height-3" placeholder="Select one" label="timezoneName" track-by="timezoneName"></multiselect>
              <p
                  v-for="error of v$.order.orderTimezone.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
              >
                {{error.$message}}
              </p>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="mb-3">
              <label>LPO</label>
              <input
                  v-model="order.lpo"
                  name="name"
                  type="text"
                  class="form-control py-3"
                  :class="{
                        'is-invalid': submitted && v$.order.lpo.$error,
                      }"
              />
              <p
                  v-for="error of v$.order.lpo.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
              >
                {{error.$message}}
              </p>
            </div>
          </div>
          <div v-if="products_to_order.length > 0">
            <div class="row"  v-for="(p, r) in order.items"
                 :key="r">
              <div class="mb-3 col-sm-4">
                <label>Product</label>
                <multiselect
                    v-model="order.items[r].itemId" :options="products_to_order"  class="multiselect-height-3"
                    placeholder="Select one" label="productName" track-by="productName" :searchable="true">
                  <span slot="noResult">No product found. Change search query.</span>
                </multiselect>
              </div>
              <div class="mb-3 col-sm-3" v-if="checkForPermission(order_add_warehouse_permission)">
                <label>Warehouse</label>
                <multiselect
                    v-model="order.items[r].warehouseId" :options="warehouses.objects" class="multiselect-height-3"
                    placeholder="Select one" label="name" track-by="name"></multiselect>
              </div>
              <div class="mb-3 col-sm-2">
                <label>Quantity</label>
                <input
                    v-model="order.items[r].itemCount"
                    type="number"
                    class="form-control py-3"
                />
              </div>
              <div class="mb-3 col-sm-3 form-group align-self-end">
                <button
                    class="btn rounded-circle btn-dark mx-2"
                    v-if="r > 0"
                    @click.prevent="removeProducts(order.items, r)"
                >
                  <i class="bx bx-minus"></i>
                </button>
                <button
                    class="btn rounded-circle btn-primary mx-2"
                    v-if="r == order.items.length - 1"
                    @click.prevent="addProducts(order.items)"
                >
                  <i class="bx bx-plus"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        <p v-if="error"
           style="color: #D13704;"
        >
          {{error}}
        </p>
        <div v-for="(error, index) in ordersErrors" :key="index" style="color: #D13704;">
          {{error.message}}
        </div>
        <div class="mt-3 align-middle">
          <b-button variant="primary" size="lg" class="py-3 h3 w-100" disabled v-if="ordersLoading">
            <i class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
            Loading
          </b-button>
          <b-button variant="primary" size="lg" class="py-3 h3 text-white w-100" type="submit" v-else>
            Create Customer Order
          </b-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Multiselect from 'vue-multiselect'
import {
  ALL_CUSTOMER_BRANCHES_SANS_PRODUCTS,
  ALL_PRODUCTS_SIMPLE,
  ALL_TIMEZONES,
  ALL_WAREHOUSES,
  SIMPLE_SINGLE_CUSTOMER_BRANCH
} from "@/gql";
import {mapGetters, mapActions} from "vuex";
import moment from 'moment'
import DatePicker from '@sum.cumo/vue-datepicker'
import '@sum.cumo/vue-datepicker/dist/Datepicker.css'
import TableLoading from "@/components/TableLoading";
import {permissions} from "@/router/permissions";

export default {
name: "create-order-form-component",
  setup () {
    return { v$: useVuelidate() }
  },
  components: {
    TableLoading,
    Multiselect, DatePicker
  },
  data(){
    return{
      order:{
        customer: "",
        orderedBy: "",
        deliveryDate: "",
        lpo: "",
        orderTimezone: "",
        items: [{ itemId: "", warehouseId: "" , itemCount: ""}]
      },
      order_add_warehouse_permission: permissions.order_add_warehouse_permission,
      ignore_order_timezone_permission: permissions.ignore_order_timezone_permission,
      customer: "",
      selectedBranch: "",
      deliveryDate: "",
      longi: "",
      lat: "",
      products_to_order: [],
      disabledDates: {
        days: [0],
        to: new Date(moment().format('YYYY-MM-DD')),
      },
      branchPage: 0,
      branches: {
        page: 0,
        objects: []
      },
      branch: {
        publicId: 0,
        validOrderProducts: []
      },
      products: {
        objects: []
      },
      warehouses: {
        objects: []
      },
      timezones: {
        objects: []
      },
      availableTimezones: [],
      productSearchKeyword: "",
      branchSearchKeyword: "",
      submitted: false,
      doIgnoreTimezone: false,
      error: "",
    }
  },
  validations () {
    return {
      order:{
        customer: {required},
        orderedBy: {required},
        lpo: {required},
        orderTimezone: {required},
        items: [
            {
              itemId: {required},
              itemCount: {required}
            }
        ]
      },
      deliveryDate: {required},
    }
  },
  apollo: {
    branches: {
      query: ALL_CUSTOMER_BRANCHES_SANS_PRODUCTS,
      variables(){
        return {
          page: 0,
          perPage: 75,
          branchSearchKeyword: this.branchSearchKeyword
        }
      }
    },
    products: {
      query: ALL_PRODUCTS_SIMPLE,
      variables(){
        return {
          page: 1,
          perPage: 30,
          searchKeyword: this.productSearchKeyword
        }
      }
    },
    branch: {
      query: SIMPLE_SINGLE_CUSTOMER_BRANCH,
      variables(){
        return {
          id: this.selectedBranch
        }
      }
    },
    warehouses: {
      query: ALL_WAREHOUSES,
      variables(){
        return {
          page: 1,
          perPage:30
        }
      }
    },
    timezones: {
      query: ALL_TIMEZONES,
      variables(){
        return {
          page: 1,
          perPage:30
        }
      }
    }
  },
  computed: {
    ... mapGetters({
      ordersLoading: 'orders/ordersLoading',
      ordersErrors: 'orders/ordersErrors'
    }),
  },
  watch: {
    customer(){
      this.customerChange()
      this.clearDeliveryDate()
    },
    deliveryDate(){
      this.checkOrderTimezones()
    },
    branch(){
      this.branchSelected()
    }
  },
  methods: {
  ... mapActions({
    checkAvailableTimezone: "orders/checkAvailableTimezone"
  }),
    async createOrder() {
      const isFormCorrect = await this.v$.$validate()
      if (!isFormCorrect) {
        this.error = "There is an empty field"
        return
      }

      const new_items = []
      this.order.items.forEach((item) => {
        new_items.push({'itemId': item.itemId.publicId, 'itemCount': parseInt(item.itemCount), 'warehouseId': item.warehouseId.publicId})
      })
      const data = {
        ...this.order,
        customer: this.order.customer.publicId,
        orderTimezone: this.order.orderTimezone.publicId,
        deliveryDate: moment(this.deliveryDate).format('YYYY-MM-DD'),
        ignoreTimezoneLimit: this.doIgnoreTimezone,
        items: new_items
      }
      this.$emit('orderAction', data)
      this.error = ""
      this.v$.$touch();
    },
    removeProducts(p, i) {
      p.splice(i, 1);
    },
    addProducts(p) {
      p.push({ itemId: "", warehouseId: "" , itemCount: ""});
    },
    addRecommendedProducts(){
      this.order.items.push({ itemId: { "value": "6", "label": "goal" }, warehouseId:  { "value": "1", "label": "KEA Trading LTD" } , itemCount: 67});
    },
    asyncFindProduct (query) {
      this.productSearchKeyword = query
    },
    asyncFindBranch (query) {
    if (query){
      this.branches.objects =  []
      this.branchSearchKeyword = query
      this.branchPage = 0
    }
    },
    customerChange(){
      this.order.customer = this.customer
      this.selectedBranch = this.customer.publicId
      this.products_to_order = []
      this.longi = ""
      this.lat = ""
      this.disabledDates =  {
        days: [0],
        to: new Date(moment().format('YYYY-MM-DD'))
      }

    },
    branchSelected(){
      if(this.branch !== null){
        this.products_to_order = this.branch.validOrderProducts
        this.longi = this.branch.longi
        this.lat = this.branch.lat

        // get days to remove from date picker
        const days = [0,1,2,3,4,5,6,7]
        this.branch.validOrderDays.forEach((day) =>{
          const index = days.indexOf(day.calendarCode);
          if (index > -1) {
            days.splice(index, 1);
          }
        })

        this.disabledDates.days = days

        this.checkOrderTimezones()
      }
    },
    clearDeliveryDate(){
    this.deliveryDate = ""
    },
    async checkOrderTimezones(){
      if (this.longi !== "" && this.deliveryDate !== ""){
        const data = {
          deliveryDate: moment(this.deliveryDate).format('YYYY-MM-DD'),
          long: parseFloat(this.longi),
          lat: parseFloat(this.lat),
          branchId: this.customer.publicId
        }
        if (this.doIgnoreTimezone){
          this.availableTimezones = this.timezones.objects
        }else {
          await this.checkAvailableTimezone(data).then(resp => {
            this.availableTimezones = resp
          })
        }
      }
    },
    ignoreTimezone(event){
      this.doIgnoreTimezone = event.target.checked
      this.checkOrderTimezones()
    }
  },
}
</script>

<style scoped>

</style>
